import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, type AttemptResult, type Progress, type Stats } from "../api.ts";
import { BANK } from "../bank/index.ts";
import { dueCount, inTracks, pickNext, queueFor, type Queued } from "../scheduler.ts";
import { DrillScreen } from "../screens/DrillScreen.tsx";
import { analytics } from "../analytics.ts";
import { reportError } from "../instrument.ts";
import { usePageMeta } from "../components/Layout.tsx";
import { useSession } from "../session.tsx";

/**
 * The practice route. Which drill you get is decided here on mount from server
 * state, so a refresh is a legitimate way to ask for the next one and nothing
 * about the choice is held in the browser.
 *
 * With a drill named in the URL that one is served instead of the scheduler's
 * pick. The choice is deliberately not sticky: finishing it hands you back to
 * /drill, because a chosen problem is a detour rather than a new mode. An id
 * that is not in the bank falls through to the scheduler rather than erroring,
 * since a stale link should still give you something to practise.
 */
/**
 * What a guest sees in place of their totals. Every field is zero because none
 * of them exist without an account, and the drill header hides the row anyway;
 * this is here so the screen has the same shape either way rather than making
 * every field optional for one caller.
 */
const GUEST_STATS: Stats = {
  dayStreak: 0,
  totalScore: 0,
  totalAttempts: 0,
  todayAttempts: 0,
  todayPasses: 0,
  todayScore: 0,
  dueNow: 0,
};

export function Drill() {
  usePageMeta("Practise");
  const { drillId } = useParams();
  const { user, loading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<Map<string, Progress>>(new Map());
  const [stats, setStats] = useState<Stats | null>(null);
  const [queued, setQueued] = useState<Queued | null>(null);
  const [failed, setFailed] = useState(false);
  // Only drills from tracks this user enrolled in are ever candidates.
  const [bank, setBank] = useState(BANK);
  // Everything already served this sitting. Skip walks forward through the bank
  // rather than bouncing between the same two problems.
  const [servedThisSession, setServedThisSession] = useState<string[]>([]);

  useEffect(() => {
    // Wait for the session to resolve. Deciding while it is in flight would
    // hand a signed-in user the guest screen on every refresh, which is the
    // same bug `RequireAuth` exists to avoid.
    if (sessionLoading) return;
    let cancelled = false;
    // Cleared before the round trip, so moving between a chosen drill and the
    // scheduler shows the loading state rather than the previous problem's
    // finished verdict until the fetch lands.
    setQueued(null);

    // Guests get the drill and none of the bookkeeping. There is no round trip
    // at all: the bank, the runner and the scheduler are already in the browser,
    // and an empty progress map is a truthful description of someone with no
    // history rather than a special case.
    if (!user) {
      const enrolled = inTracks(BANK, undefined);
      const map = new Map<string, Progress>();
      const chosen = drillId ? BANK.find((d) => d.id === drillId) : undefined;
      const next = chosen ? queueFor(chosen, map, new Date()) : pickNext(enrolled, map, new Date());
      setBank(enrolled);
      setProgress(map);
      setStats(GUEST_STATS);
      setQueued(next);
      if (next) {
        setServedThisSession([next.drill.id]);
        analytics.drillServed({
          drillId: next.drill.id,
          pattern: next.drill.pattern,
          reason: next.reason,
        });
      }
      return;
    }

    Promise.all([api.state(), api.profile().catch(() => null)])
      .then(([{ progress: rows, stats: loaded }, profile]) => {
        if (cancelled) return;
        const enrolled = inTracks(BANK, profile?.profile.tracks);
        setBank(enrolled);
        const map = new Map(rows.map((r) => [r.drillId, r]));
        // A named drill is honoured even when it belongs to a track this user
        // has not enrolled in. Enrolment is about what gets served unasked; it
        // is not a reason to refuse something somebody explicitly opened.
        const chosen = drillId ? BANK.find((d) => d.id === drillId) : undefined;
        const next = chosen ? queueFor(chosen, map, new Date()) : pickNext(enrolled, map, new Date());
        setProgress(map);
        setStats(loaded);
        setQueued(next);
        if (next) setServedThisSession([next.drill.id]);
        if (next) {
          analytics.drillServed({
            drillId: next.drill.id,
            pattern: next.drill.pattern,
            reason: next.reason,
          });
        }
      })
      .catch((cause) => {
        if (cancelled) return;
        reportError(cause, { where: "Drill.load" });
        setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [drillId, user, sessionLoading]);

  const onGraded = useCallback((result: AttemptResult) => {
    setProgress((prev) => new Map(prev).set(result.progress.drillId, result.progress));
    setStats(result.stats);
  }, []);

  const onNext = useCallback(() => {
    // Leaving a chosen drill goes back to the scheduler rather than picking the
    // next one here, so the URL never claims you are still on the problem you
    // opened and the schedule takes over again as soon as the detour is done.
    if (drillId) {
      navigate("/drill");
      return;
    }
    const now = new Date();
    setQueued((current) => {
      const seen = current ? [...servedThisSession, current.drill.id] : servedThisSession;
      const next = pickNext(bank, progress, now, seen);
      if (next) {
        setServedThisSession([...seen, next.drill.id]);
        analytics.drillServed({
          drillId: next.drill.id,
          pattern: next.drill.pattern,
          reason: next.reason,
        });
      }
      return next;
    });
  }, [bank, progress, servedThisSession, drillId, navigate]);

  const due = useMemo(() => (stats ? dueCount(bank, progress, new Date()) : 0), [bank, progress, stats]);

  if (failed) {
    return (
      <div className="centred">
        <div className="panel form">
          <h1>Offline</h1>
          <p className="muted">Could not reach the server.</p>
          <button className="primary" onClick={() => location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!queued || !stats) return <div className="centred muted">Loading…</div>;

  return (
    <DrillScreen
        key={queued.drill.id + (queued.progress?.lastAttemptAt ?? "")}
      queued={queued}
      stats={stats}
      dueNow={due}
      onGraded={onGraded}
      onNext={onNext}
      guest={!user}
    />
  );
}
